import {
  checkSyncHealth,
  fetchSyncRevision,
  loadAccountSession,
  loadSyncSettings,
  loginNanodeAccount,
  logoutNanodeAccount,
  normalizeSyncBaseUrl,
  parseSnapshot,
  pullAccountCareer,
  pullSyncLive,
  pullSyncState,
  saveAccountSession,
  saveSyncSettings,
  syncConfigured,
  syncStatusWhileProbing,
  type CareerSaveBlob,
  type NanodeAccountSession,
  type SyncClientSettings,
  type SyncLinkStatus,
  type SyncLiveSlice,
  type SyncRevisionMeta,
  type SyncSnapshot,
} from "@northstar/dispatch-core";
import {
  companionRuntimeApiBase,
  isHostedCompanionApiBase,
  resolveCompanionSyncBase,
} from "./apiBase";
import { COMPANION_SNAPSHOT_ACCOUNT_KEY, COMPANION_SNAPSHOT_KEY } from "./companion";

export type CompanionPullResult =
  | { ok: true; snapshot: SyncSnapshot }
  | { ok: false; error: string; status?: number };

export function companionStorage(): Storage | null {
  return typeof localStorage !== "undefined" ? localStorage : null;
}

export function companionHosted(): boolean {
  return Boolean(companionRuntimeApiBase());
}

/** Capacitor always uses the absolute Pages proxy. A stored `/api` must not win. */
export function companionCallBase(explicit: string): string {
  const runtime = companionRuntimeApiBase();
  if (runtime) return runtime;
  const normalized = explicit.trim().replace(/\/+$/, "");
  return normalized;
}

export function initialCompanionSettings(storage?: Pick<Storage, "getItem"> | null): SyncClientSettings {
  const loaded = loadSyncSettings(storage);
  const runtime = companionRuntimeApiBase();
  if (runtime) return { ...loaded, baseUrl: runtime };
  return { ...loaded, baseUrl: resolveCompanionSyncBase(loaded.baseUrl, false) };
}

export function companionCanProbe(settings: SyncClientSettings): boolean {
  return Boolean(normalizeSyncBaseUrl(settings.baseUrl) || companionHosted());
}

/** Hosted Pages: account session is enough — the Function holds SYNC_TOKEN. LAN still needs the company token. */
export function companionCanPull(
  settings: SyncClientSettings,
  account: NanodeAccountSession | null,
): boolean {
  if (!account?.token) return false;
  const base = resolveCompanionSyncBase(settings.baseUrl, companionHosted());
  if (isHostedCompanionApiBase(base)) return true;
  return syncConfigured({ ...settings, baseUrl: base });
}

function syncTokenFor(
  settings: SyncClientSettings,
  account: NanodeAccountSession | null,
): { baseUrl: string; token: string } | { error: string } {
  const baseUrl = companionCallBase(resolveCompanionSyncBase(settings.baseUrl, companionHosted()));
  if (isHostedCompanionApiBase(baseUrl)) {
    if (!account?.token) return { error: "Sign in to pull a crew snapshot." };
    return { baseUrl, token: account.token };
  }
  if (!account?.token) return { error: "Sign in with the same Nanode account as the desk." };
  if (!syncConfigured({ ...settings, baseUrl })) {
    return { error: "Set the sync server URL and API token to pull a crew snapshot." };
  }
  return { baseUrl, token: settings.token };
}

export function loadCachedSnapshot(
  storage?: Pick<Storage, "getItem"> | null,
  accountId?: string,
): SyncSnapshot | null {
  try {
    if (!accountId) return null;
    const owner = storage?.getItem(COMPANION_SNAPSHOT_ACCOUNT_KEY) ?? "";
    if (owner !== accountId) return null;
    const raw = storage?.getItem(COMPANION_SNAPSHOT_KEY);
    if (!raw) return null;
    const parsed = parseSnapshot(JSON.parse(raw));
    return parsed.ok ? parsed.snapshot : null;
  } catch {
    return null;
  }
}

export function saveCachedSnapshot(
  snapshot: SyncSnapshot,
  storage?: Pick<Storage, "setItem"> | null,
  accountId?: string,
): void {
  try {
    storage?.setItem(COMPANION_SNAPSHOT_KEY, JSON.stringify(snapshot));
    if (accountId) storage?.setItem(COMPANION_SNAPSHOT_ACCOUNT_KEY, accountId);
  } catch {
    /* Quota or private mode. The trips stay on screen; the next pull retries. */
  }
}

export type CompanionEmptyScheduleCode =
  | "never-received"
  | "server-empty"
  | "crew-unmatched"
  | "nothing-assigned"
  | "offline";

export type CompanionEmptySchedule = {
  code: CompanionEmptyScheduleCode;
  title: string;
  body: string;
  companyTripCount: number;
};

/**
 * Why the phone schedule has no trips. A list with trips returns null.
 * Offline with a saved board is not empty — the caller still shows those trips.
 */
export function explainEmptyCompanionSchedule(input: {
  hasSnapshot: boolean;
  offline?: boolean;
  companyTripCount: number;
  visibleTripCount: number;
  crewMatched: boolean;
}): CompanionEmptySchedule | null {
  if (input.visibleTripCount > 0) return null;
  const companyTripCount = Math.max(0, Math.floor(input.companyTripCount) || 0);
  if (!input.hasSnapshot) {
    if (input.offline) {
      return {
        code: "offline",
        title: "Offline",
        body: "No schedule has been saved on this phone yet. Connect to Nanode to load your company.",
        companyTripCount,
      };
    }
    return {
      code: "never-received",
      title: "Not loaded yet",
      body: "Not loaded yet. Connecting to Nanode…",
      companyTripCount,
    };
  }
  if (companyTripCount === 0) {
    return {
      code: "server-empty",
      title: "No trips scheduled",
      body: "Your company has no trips scheduled.",
      companyTripCount,
    };
  }
  if (!input.crewMatched) {
    return {
      code: "crew-unmatched",
      title: "No crew link",
      body: "You're not linked to a crew member. Ask the dispatcher to link your account (Settings → Crew).",
      companyTripCount,
    };
  }
  const trips = companyTripCount === 1 ? "1 trip" : `${companyTripCount} trips`;
  return {
    code: "nothing-assigned",
    title: "Nothing assigned to you",
    body: `Nothing assigned to you. There are ${trips} on the company board.`,
    companyTripCount,
  };
}

export function clearCachedSnapshot(storage?: Pick<Storage, "removeItem"> | null): void {
  storage?.removeItem(COMPANION_SNAPSHOT_KEY);
  storage?.removeItem(COMPANION_SNAPSHOT_ACCOUNT_KEY);
}

/** GET /v1/state. Companion never PUTs the whole board. Hosted uses the account Bearer; the proxy swaps in SYNC_TOKEN. */
export async function pullCompanionSnapshot(
  settings: SyncClientSettings,
  account?: NanodeAccountSession | null,
): Promise<CompanionPullResult> {
  const creds = syncTokenFor(settings, account ?? null);
  if ("error" in creds) return { ok: false, error: creds.error };
  return pullSyncState(creds.baseUrl, creds.token);
}

export async function pullCompanionRevision(
  settings: SyncClientSettings,
  account?: NanodeAccountSession | null,
): Promise<{ ok: true; revision: SyncRevisionMeta } | { ok: false; error: string; status?: number }> {
  const creds = syncTokenFor(settings, account ?? null);
  if ("error" in creds) return { ok: false, error: creds.error };
  return fetchSyncRevision(creds.baseUrl, creds.token);
}

export async function pullCompanionLive(
  settings: SyncClientSettings,
  account?: NanodeAccountSession | null,
  opts: { trail?: boolean } = {},
): Promise<{ ok: true; live: SyncLiveSlice } | { ok: false; error: string; status?: number }> {
  const creds = syncTokenFor(settings, account ?? null);
  if ("error" in creds) return { ok: false, error: creds.error };
  return pullSyncLive(creds.baseUrl, creds.token, opts);
}

/** Posts this crew's duty sessions. The server keeps every other crew's clock. */
export async function writeCompanionDuty(
  settings: SyncClientSettings,
  account: NanodeAccountSession | null,
  input: { crewId: string; duty: unknown },
): Promise<{ ok: true; summary: string } | { ok: false; error: string; status?: number }> {
  if (!account?.token) return { ok: false, error: "Sign in to write the company board." };
  const root = normalizeSyncBaseUrl(companionCallBase(account.baseUrl || settings.baseUrl));
  if (!root) return { ok: false, error: "Set the sync server URL to write duty." };
  try {
    const res = await fetch(`${root}/v1/board/ticket`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${account.token}`,
      },
      body: JSON.stringify({ action: "duty", crewId: input.crewId, duty: input.duty }),
    });
    const body = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string; summary?: string };
    if (!res.ok || body.ok === false) {
      return { ok: false, error: body.error || `Duty write failed (${res.status})`, status: res.status };
    }
    return { ok: true, summary: body.summary || "Duty updated." };
  } catch (err) {
    const message = err instanceof Error ? err.message : "Duty write failed.";
    return { ok: false, error: message };
  }
}

/** Pilot reply on the existing board ticket. The packet is the telex text, never a logon. */
export async function writeCompanionAcars(
  settings: SyncClientSettings,
  account: NanodeAccountSession | null,
  input: { missionId: string; packet: string; id?: string },
): Promise<{ ok: true; summary: string; missionId: string } | { ok: false; error: string; status?: number }> {
  if (!account?.token) return { ok: false, error: "Sign in to write the company board." };
  const root = normalizeSyncBaseUrl(companionCallBase(account.baseUrl || settings.baseUrl));
  if (!root) return { ok: false, error: "Set the sync server URL to write a message." };
  const id = (input.id || `cma-${Date.now().toString(36)}`).replace(/[^a-zA-Z0-9:_-]/g, "").slice(0, 80);
  try {
    const res = await fetch(`${root}/v1/board/ticket`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${account.token}`,
      },
      body: JSON.stringify({ action: "acars", missionId: input.missionId, id, packet: input.packet }),
    });
    const body = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string; summary?: string; missionId?: string };
    if (!res.ok || body.ok === false) {
      return { ok: false, error: body.error || `Message was not filed (${res.status})`, status: res.status };
    }
    return { ok: true, summary: body.summary || "Message filed.", missionId: body.missionId || input.missionId };
  } catch (err) {
    const message = err instanceof Error ? err.message : "Message was not filed.";
    return { ok: false, error: message };
  }
}

export async function writeCompanionTicket(
  settings: SyncClientSettings,
  account: NanodeAccountSession | null,
  input: {
    missionId: string;
    action: "cancel" | "edit" | "reassign_crew";
    cancelReason?: string;
    cancelReasonNote?: string;
    departureIso?: string;
    arrivalIso?: string;
    paxCount?: number;
    cargoLb?: number;
    originIcao?: string;
    destIcao?: string;
    crewHint?: string;
    acceptMissingScenery?: boolean;
  },
): Promise<{ ok: true; summary: string; missionId: string } | { ok: false; error: string; status?: number }> {
  if (!account?.token) return { ok: false, error: "Sign in to write the company board." };
  const root = normalizeSyncBaseUrl(companionCallBase(account.baseUrl || settings.baseUrl));
  if (!root) return { ok: false, error: "Set the sync server URL to write a ticket." };
  try {
    const res = await fetch(`${root}/v1/board/ticket`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${account.token}`,
      },
      body: JSON.stringify(input),
    });
    const body = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string; summary?: string; missionId?: string };
    if (!res.ok || body.ok === false) {
      return { ok: false, error: body.error || `Board write failed (${res.status})`, status: res.status };
    }
    return { ok: true, summary: body.summary || "Board updated.", missionId: body.missionId || input.missionId };
  } catch (err) {
    const message = err instanceof Error ? err.message : "Board write failed.";
    return { ok: false, error: message };
  }
}

export async function pullCompanionCareer(
  settings: SyncClientSettings,
  account: NanodeAccountSession,
): Promise<{ ok: true; blob: CareerSaveBlob } | { ok: false; error: string; status?: number }> {
  const baseUrl = companionCallBase(account.baseUrl || settings.baseUrl);
  return pullAccountCareer(baseUrl, account.token);
}

export async function probeCompanionSync(
  baseUrl: string,
  current: SyncLinkStatus,
): Promise<{ status: SyncLinkStatus; error: string; service?: string }> {
  const url = companionCallBase(resolveCompanionSyncBase(baseUrl, companionHosted()));
  if (!url) return { status: "unset", error: "" };
  const probing = syncStatusWhileProbing(current);
  void probing;
  const result = await checkSyncHealth(url);
  if (result.ok) return { status: "ok", error: "", service: result.service };
  return { status: "error", error: result.error };
}

export {
  loadAccountSession,
  loadSyncSettings,
  loginNanodeAccount,
  logoutNanodeAccount,
  saveAccountSession,
  saveSyncSettings,
  syncConfigured,
  type NanodeAccountSession,
  type SyncClientSettings,
  type SyncLinkStatus,
};
